// Revoking right after click() can race the browser's save and corrupt the download;
// keep the blob URL alive long enough for the download to start.
const BLOB_URL_REVOKE_DELAY_MS = 2000;

/**
 * Flow Entity Download Utilities
 * Handles preview and download operations for flow entities (processes, workflows)
 */

export interface PreviewOptions {
  entityType: 'process' | 'app' | 'processthread' | 'processelement';
  entityRecordID: number;
  format: 'json' | 'html';
  apiBaseUrl: string;
  tenantId?: string;
}

export interface DownloadOptions extends PreviewOptions {
  filename?: string;
}

/**
 * Validates download/preview options before making API calls
 * Returns validation result with error messages if invalid
 */
export function validateOptions(options: {
  entityType: string;
  entityRecordID: number;
  format: string;
  apiBaseUrl: string;
}): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Validate entityRecordID
  if (!Number.isInteger(options.entityRecordID) || options.entityRecordID <= 0) {
    errors.push('Entity ID must be a positive integer');
  }

  // Validate entityType
  const validEntityTypes = ['process', 'app', 'processthread', 'processelement'];
  if (!validEntityTypes.includes(options.entityType)) {
    errors.push(`Invalid entity type: ${options.entityType}. Must be one of: ${validEntityTypes.join(', ')}`);
  }

  // Validate format
  const validFormats = ['json', 'html'];
  if (!validFormats.includes(options.format)) {
    errors.push(`Invalid format: ${options.format}. Must be one of: ${validFormats.join(', ')}`);
  }

  // Validate apiBaseUrl
  if (!options.apiBaseUrl || typeof options.apiBaseUrl !== 'string') {
    errors.push('API base URL is required and must be a string');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Formats error messages based on HTTP status code or error object
 * Returns user-friendly error message
 */
export function formatErrorMessage(status?: number, error?: Error): string {
  if (status !== undefined && status !== null) {
    switch (status) {
      case 400:
        return 'Invalid parameters. Please check your selection.';
      case 401:
        return 'Not authorized. Please log in again.';
      case 403:
        return 'You don\'t have permission to download this entity.';
      case 404:
        return 'Entity not found. It may have been deleted.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service unavailable. Please try again later.';
      case 0:
        return 'Network error. Please check your connection.';
      default:
        return `Error (${status}): Please try again.`;
    }
  }

  if (error?.message) {
    return `Error: ${error.message}`;
  }

  return 'An unknown error occurred. Please try again.';
}

/**
 * Fetch preview content for a flow entity
 * Returns the content as text for modal preview display
 */
export async function getPreviewFlowEntity(
  options: PreviewOptions
): Promise<{ content: string; error?: string }> {
  try {
    // Validate options before making API call
    const validation = validateOptions(options);
    if (!validation.valid) {
      return { content: '', error: validation.errors.join('; ') };
    }

    const { entityType, entityRecordID, format, apiBaseUrl } = options;

    // Build URL with query parameters
    const url = new URL('/api/v1/expressions/multiquery/exports/DownloadFlowEntity', apiBaseUrl);
    url.searchParams.set('entityType', entityType);
    url.searchParams.set('entityRecordID', String(entityRecordID));
    url.searchParams.set('outputFormat', format);

    // Get auth token from localStorage (same pattern as baseApiClient)
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Accept': format === 'json' ? 'application/json' : 'text/html',
    };

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      const userFriendlyError = formatErrorMessage(response.status);
      return { content: '', error: userFriendlyError };
    }

    const content = await response.text();
    return { content };
  } catch (error: any) {
    const userFriendlyError = formatErrorMessage(undefined, error);
    return { content: '', error: userFriendlyError };
  }
}

/**
 * Download a flow entity as a file
 * Fetches blob and triggers browser download
 */
export async function downloadFlowEntity(
  options: DownloadOptions
): Promise<{ success: boolean; error?: string }> {
  try {
    // Validate options before making API call
    const validation = validateOptions(options);
    if (!validation.valid) {
      return { success: false, error: validation.errors.join('; ') };
    }

    const { entityType, entityRecordID, format, apiBaseUrl, filename: customFilename } = options;

    // Build URL with query parameters
    const url = new URL('/api/v1/expressions/multiquery/exports/DownloadFlowEntity', apiBaseUrl);
    url.searchParams.set('entityType', entityType);
    url.searchParams.set('entityRecordID', String(entityRecordID));
    url.searchParams.set('outputFormat', format);

    // Get auth token from localStorage (same pattern as baseApiClient)
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Accept': format === 'json' ? 'application/json' : 'text/html',
    };

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      const userFriendlyError = formatErrorMessage(response.status);
      return { success: false, error: userFriendlyError };
    }

    const blob = await response.blob();

    // Generate filename if not provided
    const filename = customFilename || `${entityType}_${entityRecordID}.${format}`;

    // Create download link
    const url_object = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url_object;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup
    try {
      setTimeout(() => URL.revokeObjectURL(url_object), BLOB_URL_REVOKE_DELAY_MS);
    } catch (e) {
      console.warn('Failed to revoke object URL:', e);
    }

    return { success: true };
  } catch (error: any) {
    const userFriendlyError = formatErrorMessage(undefined, error);
    return { success: false, error: userFriendlyError };
  }
}
